import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import * as api from '../services/api';
import { subscribeToRefresh } from '../utils/dataSync';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const VisualPlanner = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  const startingDayOfWeek = firstDayOfMonth.getDay();
  const daysInMonth = lastDayOfMonth.getDate();

  useEffect(() => {
    loadJobs();
  }, []);

  // Subscribe to centralized data sync - auto refresh when jobs change
  useEffect(() => {
    const unsubscribe = subscribeToRefresh('visualplanner', (detail) => {
      console.log('[VisualPlanner] Refresh triggered by:', detail.source);
      loadJobs();
    });
    return unsubscribe;
  }, []);

  const loadJobs = async () => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/jobs?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const prevMonth = function() {
    setCurrentDate(new Date(year, month - 1, 1));
  };
  
  const nextMonth = function() {
    setCurrentDate(new Date(year, month + 1, 1));
  };
  
  const getJobsForDate = function(day) {
    const dateStr = year + '-' + String(month + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
    return jobs.filter(function(job) { 
      return job.scheduled_date === dateStr || job.next_due === dateStr;
    });
  };
  
  // Generate calendar days
  const emptyDays = [];
  for (let i = 0; i < startingDayOfWeek; i++) {
    emptyDays.push(i);
  }
  
  const monthDays = [];
  for (let day = 1; day <= daysInMonth; day++) {
    monthDays.push(day);
  }
  
  const selectedDateJobs = selectedDate ? getJobsForDate(selectedDate) : [];

  const today = new Date();
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = today.getDate();

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50">
      <div className="bg-zinc-900 text-white p-4" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 16px)' }}>
        <div className="flex items-center justify-between">
          <button 
            onClick={prevMonth}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <h2 className="text-xl font-semibold">
            {months[month]} {year}
          </h2>
          <button 
            onClick={nextMonth}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto p-4">
        <Card className="border-zinc-200">
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {daysOfWeek.map(function(day) {
                return (
                  <div key={day} className="text-center text-sm font-medium text-zinc-500 py-2">
                    {day}
                  </div>
                );
              })}
            </div>
            
            <div className="grid grid-cols-7 gap-1">
              {emptyDays.map(function(i) {
                return <div key={'empty-' + i} className="aspect-square" />;
              })}
              
              {monthDays.map(function(day) {
                const dayJobs = getJobsForDate(day);
                const hasJobs = dayJobs.length > 0;
                const isSelected = selectedDate === day;
                const isToday = isCurrentMonth && day === todayDate;
                
                return (
                  <button
                    key={day}
                    onClick={function() { setSelectedDate(day); }}
                    className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-colors relative ${
                      isSelected 
                        ? 'bg-emerald-500 text-white' 
                        : isToday 
                          ? 'bg-emerald-100 text-emerald-700 font-bold'
                          : 'hover:bg-zinc-100'
                    }`}
                  >
                    <span className={`text-sm font-medium ${isSelected ? 'text-white' : ''}`}>
                      {day}
                    </span>
                    {hasJobs && (
                      <div className="flex gap-0.5 mt-1">
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          isSelected ? 'bg-white' : 'bg-emerald-500'
                        }`} />
                        {dayJobs.length > 1 && (
                          <span className={`text-xs ${isSelected ? 'text-white' : 'text-emerald-600'}`}>
                            {dayJobs.length}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
        
        {selectedDate && (
          <Card className="mt-4 border-zinc-200">
            <CardHeader>
              <CardTitle className="text-zinc-900">
                {months[month]} {selectedDate}, {year}
                <span className="text-sm font-normal text-zinc-500 ml-2">
                  ({selectedDateJobs.length} job{selectedDateJobs.length !== 1 ? 's' : ''})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {selectedDateJobs.length > 0 ? (
                selectedDateJobs.map(function(job) {
                  return (
                    <div 
                      key={job.id}
                      className="p-3 rounded-xl border border-zinc-200 bg-white hover:border-emerald-200 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-zinc-900">{job.customer_name || job.title || 'Job'}</p>
                          <p className="text-sm text-zinc-500">
                            {job.services || job.description || 'Window Clean'}
                          </p>
                          {job.address && (
                            <p className="text-xs text-zinc-400 mt-1">{job.address}</p>
                          )}
                          {job.assignee && (
                            <p className="text-xs text-emerald-600 mt-1">Assigned: {job.assignee}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className="font-semibold text-zinc-900">
                            £{(job.price || 0).toFixed(2)}
                          </span>
                          {job.duration && (
                            <p className="text-xs text-zinc-400">{job.duration}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-zinc-500 text-center py-4">
                  No jobs scheduled for this date
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
      
      <button className="fixed bottom-20 right-6 w-14 h-14 bg-emerald-500 rounded-full shadow-lg flex items-center justify-center text-white hover:bg-emerald-600 transition-colors">
        <Plus size={24} />
      </button>
    </div>
  );
};

export default VisualPlanner;
